import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';

export interface ILeadAIContext {
	data: string;
}

export function useLeadAIContext() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<ILeadAIContext, Error, { leadId: string; leadServedId?: string }>({
		mutationFn: async ({ leadId, leadServedId }) => {
			if (!leadId) {
				throw new Error('Lead ID is required');
			}

			const queryParams = leadServedId ? `?leadServedId=${encodeURIComponent(leadServedId)}` : '';
			const response = await userSession.webServiceHelper.callWebServiceAsync<string>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `lead/${leadId}/AIContext${queryParams}` }),
				'GET'
			);

			return {
				data: response.value,
			};
		},
		onSuccess: (data, { leadId }) => {
			// Cache the result so subsequent calls don't refetch
			queryClient.setQueryData(['lead', leadId, 'aiContext'], data);
		},
	});
}

// Helper hook to get cached data without triggering a fetch
export function useLeadAIContextData(leadId: string | undefined) {
	const queryClient = useQueryClient();

	if (!leadId) return undefined;

	return queryClient.getQueryData<ILeadAIContext>(['lead', leadId, 'aiContext']);
}
